import React, { useMemo } from 'react';
import { useWizard } from '../../context/WizardContext';
import { ArrowLeft, ArrowRight, FileText, BarChart3, CheckCircle, AlertCircle, MessageSquare, Hash, List, ToggleLeft, Star, Calendar } from 'lucide-react';

const B4_DataRequirements = () => {
  const { dataB, nextStep, prevStep, updateDataRaw, logDecision } = useWizard();

  const qualMethods = dataB?.qualitativeMethods || {};
  const quantMethods = dataB?.quantitativeMethods || {};

  const dataRequirements = useMemo(() => {
    const requirements = {
      qualitative: [],
      quantitative: []
    };

    const hasQualMethod = (id) => qualMethods[id];

    if (hasQualMethod('thematicAnalysis') || hasQualMethod('contentAnalysis') || 
        hasQualMethod('narrativeAnalysis') || hasQualMethod('discourseAnalysis') ||
        hasQualMethod('groundedTheory') || hasQualMethod('phenomenologicalAnalysis')) {
      requirements.qualitative.push({
        type: 'long_text',
        label: 'Open-Ended Text Questions',
        description: 'Long text/paragraph responses for thematic or content analysis',
        wizardFType: 'Long Text',
        icon: FileText,
        methods: Object.entries(qualMethods)
          .filter(([k, v]) => v && k !== 'sentimentAnalysis')
          .map(([k]) => k.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase()).trim())
      });
    }

    if (hasQualMethod('sentimentAnalysis')) {
      requirements.qualitative.push({
        type: 'any_text',
        label: 'Text Responses (Any Length)',
        description: 'Short or long text for sentiment scoring',
        wizardFType: 'Short Text or Long Text',
        icon: MessageSquare,
        methods: ['Sentiment Analysis']
      });
    }

    const descEnabled = quantMethods.descriptiveStats?.enabled;
    const descCategories = quantMethods.descriptiveStats?.categories || [];
    const infEnabled = quantMethods.inferentialTests?.enabled;
    const infTests = quantMethods.inferentialTests?.tests || [];

    const needsNumeric = descEnabled && (
      descCategories.includes('central_tendency') ||
      descCategories.includes('dispersion') ||
      descCategories.includes('distribution') ||
      descCategories.includes('percentiles') ||
      descCategories.includes('outliers')
    );

    if (needsNumeric) {
      requirements.quantitative.push({
        type: 'numeric',
        label: 'Numeric/Rating Questions',
        description: 'Likert scales (1-5, 1-7), numeric inputs, slider ratings',
        wizardFType: 'Likert Scale, Number Input, Rating Scale',
        icon: Hash,
        examples: ['Rate your satisfaction (1-5)', 'Enter your age', 'How many hours per week?']
      });
    }

    const needsCategorical = descEnabled && descCategories.includes('frequency');
    const needsCatForTests = infEnabled && (
      infTests.includes('chi_square') ||
      infTests.includes('comparison_2groups') ||
      infTests.includes('comparison_3plus')
    );

    if (needsCategorical || needsCatForTests) {
      requirements.quantitative.push({
        type: 'categorical',
        label: 'Categorical/Choice Questions',
        description: 'Multiple choice, dropdown, checkbox questions',
        wizardFType: 'Multiple Choice, Dropdown, Checkbox',
        icon: List,
        examples: ['Select your department', 'Choose your education level', 'Which products do you use?']
      });
    }

    const needsBinary = infEnabled && (
      infTests.includes('chi_square') ||
      infTests.includes('comparison_2groups')
    );

    if (needsBinary) {
      requirements.quantitative.push({
        type: 'binary',
        label: 'Binary/Yes-No Questions',
        description: 'Two-option questions for group comparisons',
        wizardFType: 'Yes/No, True/False, Checkbox (2 options)',
        icon: ToggleLeft,
        examples: ['Are you a manager? (Yes/No)', 'Have you used the service before?']
      });
    }

    const needsReliability = descEnabled && descCategories.includes('reliability');

    if (needsReliability) {
      requirements.quantitative.push({
        type: 'scale_items',
        label: 'Multiple Scale Items',
        description: 'Multiple questions measuring the same construct for reliability',
        wizardFType: 'Multiple Likert Scale items',
        icon: Star,
        examples: ['3-5 items measuring "Job Satisfaction"', 'Multiple questions about "Customer Loyalty"']
      });
    }

    const needsRepeated = infEnabled && infTests.includes('repeated_measures');

    if (needsRepeated) {
      requirements.quantitative.push({
        type: 'repeated',
        label: 'Repeated Measurements',
        description: 'Same questions asked at multiple time points',
        wizardFType: 'Same numeric questions at T1, T2, T3',
        icon: Calendar,
        examples: ['Score at baseline, 3 months, 6 months', 'Pre-test and post-test measurements']
      });
    }

    return requirements;
  }, [qualMethods, quantMethods]);

  const handleNext = () => {
    updateDataRaw({
      'wizardB.dataRequirements': dataRequirements
    });

    const qualCount = dataRequirements.qualitative.length;
    const quantCount = dataRequirements.quantitative.length;
    
    logDecision(
      'Data Requirements',
      `${qualCount} qualitative, ${quantCount} quantitative requirement(s)`,
      `Identified data needs based on selected analysis methods`
    );

    nextStep();
  };

  const hasQualRequirements = dataRequirements.qualitative.length > 0;
  const hasQuantRequirements = dataRequirements.quantitative.length > 0;
  const hasAnyRequirements = hasQualRequirements || hasQuantRequirements;

  return (
    <div className="wizard-step">
      <h2>Step 5: Data Requirements</h2>
      <p className="step-description">
        Based on your selected analysis methods, here is what data you need to collect in your survey.
      </p>

      {!hasAnyRequirements ? (
        <div style={{
          background: '#fef3c7',
          border: '1px solid #fde047',
          borderRadius: '12px',
          padding: '1.5rem',
          marginBottom: '2rem',
          display: 'flex',
          alignItems: 'flex-start',
          gap: '1rem'
        }}>
          <AlertCircle size={24} color="#ca8a04" style={{ flexShrink: 0 }} />
          <div>
            <h4 style={{ margin: '0 0 0.5rem 0', color: '#854d0e' }}>No Analysis Methods Selected</h4>
            <p style={{ margin: 0, color: '#713f12' }}>
              You have not selected any analysis methods. Please go back to select at least one 
              qualitative or quantitative method to see your data requirements.
            </p>
          </div>
        </div>
      ) : (
        <>
          <div style={{
            background: '#ecfdf5',
            border: '1px solid #6ee7b7',
            borderRadius: '8px',
            padding: '1rem 1.25rem',
            marginBottom: '1.5rem',
            display: 'flex',
            alignItems: 'flex-start',
            gap: '0.75rem'
          }}>
            <CheckCircle size={20} color="#059669" style={{ flexShrink: 0, marginTop: '2px' }} />
            <div style={{ fontSize: '0.9rem', color: '#065f46' }}>
              <strong>Your Survey Checklist:</strong> Make sure your Wizard F survey includes the question types 
              listed below. This ensures your data can be properly analyzed using your chosen methods.
            </div>
          </div>

          {hasQualRequirements && (
            <div style={{ marginBottom: '2rem' }}>
              <h3 style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '0.5rem',
                color: '#7c3aed',
                marginBottom: '1rem'
              }}>
                <MessageSquare size={20} />
                Qualitative Data Requirements
              </h3>

              <div style={{ display: 'grid', gap: '1rem' }}>
                {dataRequirements.qualitative.map((req, index) => {
                  const Icon = req.icon;
                  return (
                    <div key={index} style={{
                      background: '#f5f3ff',
                      border: '1px solid #c4b5fd',
                      borderRadius: '12px',
                      padding: '1.25rem'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
                        <div style={{
                          width: '44px',
                          height: '44px',
                          borderRadius: '10px',
                          background: '#7c3aed',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0
                        }}>
                          <Icon size={22} color="white" />
                        </div>
                        <div style={{ flex: 1 }}>
                          <h4 style={{ margin: '0 0 0.25rem 0', color: '#1e293b' }}>{req.label}</h4>
                          <p style={{ margin: '0 0 0.5rem 0', fontSize: '0.9rem', color: '#475569' }}>
                            {req.description}
                          </p>
                          <div style={{
                            background: 'white',
                            borderRadius: '6px',
                            padding: '0.5rem 0.75rem',
                            fontSize: '0.85rem'
                          }}>
                            <strong style={{ color: '#7c3aed' }}>Wizard F Question Type:</strong>{' '}
                            <span style={{ color: '#1e293b' }}>{req.wizardFType}</span>
                          </div>
                          {req.methods && (
                            <div style={{ marginTop: '0.5rem', fontSize: '0.8rem', color: '#64748b' }}>
                              <strong>For:</strong> {req.methods.join(', ')}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {hasQuantRequirements && (
            <div style={{ marginBottom: '2rem' }}>
              <h3 style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '0.5rem',
                color: '#0891b2',
                marginBottom: '1rem'
              }}>
                <BarChart3 size={20} />
                Quantitative Data Requirements
              </h3>

              <div style={{ display: 'grid', gap: '1rem' }}>
                {dataRequirements.quantitative.map((req, index) => {
                  const Icon = req.icon;
                  return (
                    <div key={index} style={{
                      background: '#ecfeff',
                      border: '1px solid #67e8f9',
                      borderRadius: '12px',
                      padding: '1.25rem'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
                        <div style={{
                          width: '44px',
                          height: '44px',
                          borderRadius: '10px',
                          background: '#0891b2',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0
                        }}>
                          <Icon size={22} color="white" />
                        </div>
                        <div style={{ flex: 1 }}>
                          <h4 style={{ margin: '0 0 0.25rem 0', color: '#1e293b' }}>{req.label}</h4>
                          <p style={{ margin: '0 0 0.5rem 0', fontSize: '0.9rem', color: '#475569' }}>
                            {req.description}
                          </p>
                          <div style={{
                            background: 'white',
                            borderRadius: '6px',
                            padding: '0.5rem 0.75rem',
                            fontSize: '0.85rem',
                            marginBottom: '0.5rem'
                          }}>
                            <strong style={{ color: '#0891b2' }}>Wizard F Question Type:</strong>{' '}
                            <span style={{ color: '#1e293b' }}>{req.wizardFType}</span>
                          </div>
                          {req.examples && (
                            <div style={{ fontSize: '0.8rem', color: '#64748b' }}>
                              <strong>Examples:</strong> {req.examples.join(' | ')}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}

      <div className="nav-buttons">
        <button className="btn btn-secondary" onClick={prevStep}>
          <ArrowLeft size={18} />
          Back
        </button>
        <button className="btn btn-primary" onClick={handleNext}>
          Next Step
          <ArrowRight size={18} />
        </button>
      </div>
    </div>
  );
};

export default B4_DataRequirements;
